import React, { useState, useEffect } from 'react';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Checkbox, RadioButton, Switch, Loading, Dialog } from '@dsi/react-eds';
import notificationPreferencesService, { NotificationPreference } from '../../api/notificationPreferencesService';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { NotificationType, NotificationStage } from '../../const/NotificationTypes';
import { formatLastLoginTime } from '../../utils/dateUtils';
import styles from './SystemPanel.module.css';

interface SystemPanelProps {
  user: {
    displayName: string;
    email: string;
    expiryTime: number;
    username: string;
    roles: string[];
    lastLoginAt: string | null;
  };
  theme: boolean;
  toggleTheme: () => void;
  signOut: () => void;
  onRequestClose?: () => void;
  silentMode: boolean;
  setSilentMode: (val: boolean) => void;
}

const SystemPanel: React.FC<SystemPanelProps> = ({ user, theme, toggleTheme, signOut, onRequestClose, silentMode, setSilentMode }) => {
  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Weekly notification preferences state
  const [weeklyEmailEnabled, setWeeklyEmailEnabled] = useState(false);
  const [weeklyNotificationType, setWeeklyNotificationType] = useState<NotificationType>(NotificationType.ALL);
  const [weeklyCustomStages, setWeeklyCustomStages] = useState<NotificationStage[]>([]);

  // Daily notification preferences state
  const [dailyEmailEnabled, setDailyEmailEnabled] = useState(false);
  const [dailyNotificationType, setDailyNotificationType] = useState<NotificationType>(NotificationType.ALL);
  const [dailyCustomStages, setDailyCustomStages] = useState<NotificationStage[]>([]);

  const [preferenceId, setPreferenceId] = useState<string | null>(null);

  // UI state tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [backendState, setBackendState] = useState<NotificationPreference | null>(null);

  // Track unsaved notification settings for restoring on toggle
  const [unsavedWeeklyNotificationType, setUnsavedWeeklyNotificationType] = useState<NotificationType>(weeklyNotificationType);
  const [unsavedWeeklyCustomStages, setUnsavedWeeklyCustomStages] = useState<NotificationStage[]>(weeklyCustomStages);
  const [unsavedDailyNotificationType, setUnsavedDailyNotificationType] = useState<NotificationType>(dailyNotificationType);
  const [unsavedDailyCustomStages, setUnsavedDailyCustomStages] = useState<NotificationStage[]>(dailyCustomStages);

  // State for close prompt dialog
  const [showClosePrompt, setShowClosePrompt] = useState(false);

  const { addNotifications } = useSwobNotifications();

  // Initial load logic
  useEffect(() => {
    const loadNotificationPreferences = async () => {
      try {
        setIsLoading(true);
        const preferences = await notificationPreferencesService.getMyPreferences();

        if (preferences && preferences.length > 0) {
          // Use the first preference (assuming one per user)
          const preference = preferences[0];
          setWeeklyEmailEnabled(preference.weeklyEnabled || false);
          setWeeklyNotificationType(preference.weeklyType || NotificationType.ALL);
          setWeeklyCustomStages(preference.weeklyCustomStages || []);
          setDailyEmailEnabled(preference.dailyEnabled || false);
          setDailyNotificationType(preference.dailyType || NotificationType.ALL);
          setDailyCustomStages(preference.dailyCustomStages || []);
          setPreferenceId(preference.id || null);
          setBackendState(preference);
        } else {
          // No preference exists - set defaults
          setWeeklyEmailEnabled(false);
          setDailyEmailEnabled(false);
          setWeeklyNotificationType(NotificationType.ALL);
          setWeeklyCustomStages([]);
          setDailyNotificationType(NotificationType.ALL);
          setDailyCustomStages([]);
          setPreferenceId(null);
          setBackendState(null);
        }
      } catch (error) {
        console.error('Failed to load notification preferences:', error);
        // Show toast
        addNotifications([
          {
            title: 'Error',
            icon: 'fault',
            description: 'Failed to load notification preferences. Email notifications will be disabled.',
            timestamp: new Date(),
            color: 'red'
          }
        ]);
        // On error, default to email disabled
        setWeeklyEmailEnabled(false);
        setDailyEmailEnabled(false);
        setWeeklyNotificationType(NotificationType.ALL);
        setWeeklyCustomStages([]);
        setDailyNotificationType(NotificationType.ALL);
        setDailyCustomStages([]);
        setPreferenceId(null);
        setBackendState(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadNotificationPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.email]);

  // Weekly email switch handler
  const handleWeeklyEmailSwitch = () => {
    if (!weeklyEmailEnabled) {
      // Turning ON: restore previous unsaved selection if exists, else default
      setWeeklyEmailEnabled(true);
      setWeeklyNotificationType(unsavedWeeklyNotificationType || NotificationType.ALL);
      setWeeklyCustomStages(
        unsavedWeeklyNotificationType === NotificationType.CUSTOM && unsavedWeeklyCustomStages.length > 0
          ? unsavedWeeklyCustomStages
          : [
              NotificationStage.SW_DISTRIBUTION,
              NotificationStage.OCI_REGISTRY,
              NotificationStage.ARTIFACTS_RETRIEVING_LAB,
              NotificationStage.ARTIFACTS_ONBOARDING_LAB,
              NotificationStage.ARTIFACTS_DEPLOYMENT,
              NotificationStage.TESTING,
              NotificationStage.ARTIFACTS_RETRIEVING_PROD,
              NotificationStage.ARTIFACTS_ONBOARDING_PROD
            ]
      );
      setHasUnsavedChanges(true);
    } else {
      // Turning OFF: hide options, keep unsaved state
      setWeeklyEmailEnabled(false);
      setUnsavedWeeklyNotificationType(weeklyNotificationType);
      setUnsavedWeeklyCustomStages(weeklyCustomStages);
      setHasUnsavedChanges(true);
    }
  };

  // Daily email switch handler
  const handleDailyEmailSwitch = () => {
    if (!dailyEmailEnabled) {
      // Turning ON: restore previous unsaved selection if exists, else default
      setDailyEmailEnabled(true);
      setDailyNotificationType(unsavedDailyNotificationType || NotificationType.ALL);
      setDailyCustomStages(
        unsavedDailyNotificationType === NotificationType.CUSTOM && unsavedDailyCustomStages.length > 0
          ? unsavedDailyCustomStages
          : [
              NotificationStage.SW_DISTRIBUTION,
              NotificationStage.OCI_REGISTRY,
              NotificationStage.ARTIFACTS_RETRIEVING_LAB,
              NotificationStage.ARTIFACTS_ONBOARDING_LAB,
              NotificationStage.ARTIFACTS_DEPLOYMENT,
              NotificationStage.TESTING,
              NotificationStage.ARTIFACTS_RETRIEVING_PROD,
              NotificationStage.ARTIFACTS_ONBOARDING_PROD
            ]
      );
      setHasUnsavedChanges(true);
    } else {
      // Turning OFF: hide options, keep unsaved state
      setDailyEmailEnabled(false);
      setUnsavedDailyNotificationType(dailyNotificationType);
      setUnsavedDailyCustomStages(dailyCustomStages);
      setHasUnsavedChanges(true);
    }
  };

  // Weekly radio button handler
  const handleWeeklyRadioChange = (type: NotificationType) => {
    setWeeklyNotificationType(type);
    setHasUnsavedChanges(true);
    if (type === NotificationType.CUSTOM) {
      // If switching to custom, restore previous unsaved or select all by default
      setWeeklyCustomStages(
        unsavedWeeklyCustomStages.length > 0
          ? unsavedWeeklyCustomStages
          : [
              NotificationStage.SW_DISTRIBUTION,
              NotificationStage.OCI_REGISTRY,
              NotificationStage.ARTIFACTS_RETRIEVING_LAB,
              NotificationStage.ARTIFACTS_ONBOARDING_LAB,
              NotificationStage.ARTIFACTS_DEPLOYMENT,
              NotificationStage.TESTING,
              NotificationStage.ARTIFACTS_RETRIEVING_PROD,
              NotificationStage.ARTIFACTS_ONBOARDING_PROD
            ]
      );
    }
  };

  // Daily radio button handler
  const handleDailyRadioChange = (type: NotificationType) => {
    setDailyNotificationType(type);
    setHasUnsavedChanges(true);
    if (type === NotificationType.CUSTOM) {
      // If switching to custom, restore previous unsaved or select all by default
      setDailyCustomStages(
        unsavedDailyCustomStages.length > 0
          ? unsavedDailyCustomStages
          : [
              NotificationStage.SW_DISTRIBUTION,
              NotificationStage.OCI_REGISTRY,
              NotificationStage.ARTIFACTS_RETRIEVING_LAB,
              NotificationStage.ARTIFACTS_ONBOARDING_LAB,
              NotificationStage.ARTIFACTS_DEPLOYMENT,
              NotificationStage.TESTING,
              NotificationStage.ARTIFACTS_RETRIEVING_PROD,
              NotificationStage.ARTIFACTS_ONBOARDING_PROD
            ]
      );
    }
  };

  // Weekly checkbox handler
  const handleWeeklyCheckboxChange = (stage: NotificationStage) => {
    let updatedStages: NotificationStage[];
    if (weeklyCustomStages.includes(stage)) {
      updatedStages = weeklyCustomStages.filter((s) => s !== stage);
    } else {
      updatedStages = [...weeklyCustomStages, stage];
    }
    setWeeklyCustomStages(updatedStages);
    setHasUnsavedChanges(true);
  };

  // Daily checkbox handler
  const handleDailyCheckboxChange = (stage: NotificationStage) => {
    let updatedStages: NotificationStage[];
    if (dailyCustomStages.includes(stage)) {
      updatedStages = dailyCustomStages.filter((s) => s !== stage);
    } else {
      updatedStages = [...dailyCustomStages, stage];
    }
    setDailyCustomStages(updatedStages);
    setHasUnsavedChanges(true);
  };

  // Handler for Silent Mode checkbox
  const handleSilentModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSilentMode(e.target.checked);
    setHasUnsavedChanges(true);
  };

  // Save handler
  const handleSave = async () => {
    setIsLoading(true);
    const newPreference = {
      email: user.email,
      type: backendState?.type || NotificationType.ALL,
      customStages: backendState?.customStages || [],
      isEnabled: backendState?.isEnabled || true,
      weeklyEnabled: weeklyEmailEnabled,
      weeklyType: weeklyNotificationType,
      weeklyCustomStages: weeklyNotificationType === NotificationType.CUSTOM ? weeklyCustomStages : [],
      dailyEnabled: dailyEmailEnabled,
      dailyType: dailyNotificationType,
      dailyCustomStages: dailyNotificationType === NotificationType.CUSTOM ? dailyCustomStages : []
    };
    try {
      let savedPreference: NotificationPreference;
      if (preferenceId) {
        savedPreference = await notificationPreferencesService.update(preferenceId, user.email, newPreference);
      } else {
        savedPreference = await notificationPreferencesService.create(newPreference);
        setPreferenceId(savedPreference.id || null);
      }
      setBackendState(savedPreference);
      setHasUnsavedChanges(false);
      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: 'Notification preferences saved successfully.',
          timestamp: new Date(),
          color: 'green'
        }
      ]);
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
      // Show toast and revert UI to last saved state
      addNotifications([
        {
          title: 'Error',
          icon: 'fault',
          description: 'Failed to save notification preferences.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      if (backendState) {
        setWeeklyEmailEnabled(backendState.weeklyEnabled || false);
        setWeeklyNotificationType(backendState.weeklyType || NotificationType.ALL);
        setWeeklyCustomStages(backendState.weeklyCustomStages || []);
        setDailyEmailEnabled(backendState.dailyEnabled || false);
        setDailyNotificationType(backendState.dailyType || NotificationType.ALL);
        setDailyCustomStages(backendState.dailyCustomStages || []);
      }
      setHasUnsavedChanges(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Intercept close request
  const handlePanelClose = () => {
    if (hasUnsavedChanges) {
      setShowClosePrompt(true);
    } else if (onRequestClose) {
      onRequestClose();
    }
  };

  // Handle dialog actions
  const handlePromptDiscard = () => {
    if (backendState) {
      setWeeklyEmailEnabled(backendState.weeklyEnabled || false);
      setWeeklyNotificationType(backendState.weeklyType || NotificationType.ALL);
      setWeeklyCustomStages(backendState.weeklyCustomStages || []);
      setDailyEmailEnabled(backendState.dailyEnabled || false);
      setDailyNotificationType(backendState.dailyType || NotificationType.ALL);
      setDailyCustomStages(backendState.dailyCustomStages || []);
    }
    setHasUnsavedChanges(false);
    setShowClosePrompt(false);
    if (onRequestClose) onRequestClose();
  };

  const handleSaveOnPrompt = async () => {
    await handleSave();
    setShowClosePrompt(false);
    if (onRequestClose) onRequestClose();
  };

  // Show loading spinner while fetching data
  if (isLoading) {
    return (
      <div className="settings">
        <div className="syspanel-top">
          <span className="syspanel-title"></span>
          {onRequestClose && <i className="syspanel-close icon icon-cross" onClick={handlePanelClose} style={{ cursor: 'pointer' }}></i>}
        </div>
        <div className="row">
          <div className="column sm-12 container">
            <div className="profile">
              <i className="icon icon-profile"></i>
              <div className="username">{user.username}</div>
            </div>

            <div className="content">
              <div className="title">My Settings</div>
              <div className="item">
                <div className="left" style={{ marginBottom: '8px' }}>
                  <label>Switch Theme</label>
                </div>
                <div className="right">
                  <ReactEDSWrapper component={Switch} enabledLabel="Light" disabledLabel="Dark" onChange={toggleTheme} checked={theme} />
                </div>
              </div>

              <div className="title" style={{ marginBottom: '16px', marginTop: '20px' }}>
                Sign in
              </div>
              <div className="item">
                <div className="left">
                  {user.lastLoginAt ? `Last sign in was ${formatLastLoginTime(user.lastLoginAt)}` : 'This is your first login.'}
                </div>
              </div>

              <div className="title">My Roles</div>
              {/* <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}> */}
              <div className={styles.flexCenter}>
                <ReactEDSWrapper component={Loading} />
              </div>
              {/* </div> */}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings">
      <div className="syspanel-top">
        <span className="syspanel-title"></span>
        {/* The close button is handled by parent, so not included here */}
        {/* Example close button: */}
        {onRequestClose && <i className="syspanel-close icon icon-cross" onClick={handlePanelClose} style={{ cursor: 'pointer' }}></i>}
      </div>
      <div className="row">
        <div className="column sm-12 container">
          <div className="profile">
            <i className="icon icon-profile"></i>
            <div className="username">{user.username}</div>
          </div>

          <div className="content">
            <div className="title" style={{ marginBottom: '16px', marginTop: '24px' }}>
              My Settings
            </div>
            <div className="item">
              <div className="left">
                <label>Switch Theme</label>
              </div>
              <div className="right">
                <ReactEDSWrapper component={Switch} enabledLabel="Light" disabledLabel="Dark" onChange={toggleTheme} checked={theme} />
              </div>
            </div>

            <div className="title" style={{ marginBottom: '16px', marginTop: '20px' }}>
              Sign in
            </div>
            <div className="item">
              <div className="left">
                <label>
                  {user.lastLoginAt ? `Last sign in was ${formatLastLoginTime(user.lastLoginAt)}` : 'This is your first login.'}
                </label>
              </div>
            </div>

            <div className="title" style={{ marginBottom: '16px', marginTop: '20px' }}>
              My Roles
            </div>
            <div className="item">
              <div className="left">
                <div className={styles.roles}>
                  {user.roles.map((role) => (
                    <div key={role} className={styles.role}>
                      {role}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="title" style={{ marginBottom: '16px', marginTop: '24px' }}>
              My Notifications Triggers
            </div>
            <div className="item">
              <div className="left">
                <label>Select your stages to receive information about the status (Success and failure).</label>
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '20px 0 16px 0' }} />
            <div className="title" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', marginTop: '20px' }}>
              <span>Weekly Email</span>
              <ReactEDSWrapper
                component={Switch}
                enabledLabel="On"
                disabledLabel="Off"
                onChange={handleWeeklyEmailSwitch}
                checked={weeklyEmailEnabled}
              />
            </div>
            {weeklyEmailEnabled && (
              <div className="item">
                <div className="left">
                  <div className={styles.flexColumn}>
                    <ReactEDSWrapper
                      component={RadioButton}
                      id="weekly-rb1"
                      name="weeklyGroup"
                      defaultChecked={weeklyNotificationType === NotificationType.ALL}
                      onChange={() => handleWeeklyRadioChange(NotificationType.ALL)}
                    >
                      All Notifications
                    </ReactEDSWrapper>
                    <ReactEDSWrapper
                      component={RadioButton}
                      id="weekly-rb2"
                      name="weeklyGroup"
                      defaultChecked={weeklyNotificationType === NotificationType.FAILED_STAGES}
                      onChange={() => handleWeeklyRadioChange(NotificationType.FAILED_STAGES)}
                    >
                      Only Failed Stages
                    </ReactEDSWrapper>
                    <ReactEDSWrapper
                      component={RadioButton}
                      id="weekly-rb3"
                      name="weeklyGroup"
                      defaultChecked={weeklyNotificationType === NotificationType.CUSTOM}
                      onChange={() => handleWeeklyRadioChange(NotificationType.CUSTOM)}
                    >
                      Custom Options
                    </ReactEDSWrapper>
                  </div>
                  {weeklyNotificationType === NotificationType.CUSTOM && (
                    <div className={styles.flexColumnIndented}>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="weekly-cb1"
                        checked={weeklyCustomStages.includes(NotificationStage.SW_DISTRIBUTION)}
                        onChange={() => handleWeeklyCheckboxChange(NotificationStage.SW_DISTRIBUTION)}
                      >
                        SW Distribution
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="weekly-cb2"
                        checked={weeklyCustomStages.includes(NotificationStage.OCI_REGISTRY)}
                        onChange={() => handleWeeklyCheckboxChange(NotificationStage.OCI_REGISTRY)}
                      >
                        OCI Registry
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="weekly-cb3"
                        checked={weeklyCustomStages.includes(NotificationStage.ARTIFACTS_RETRIEVING_LAB)}
                        onChange={() => handleWeeklyCheckboxChange(NotificationStage.ARTIFACTS_RETRIEVING_LAB)}
                      >
                        Artifacts Retrieving LAB
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="weekly-cb4"
                        checked={weeklyCustomStages.includes(NotificationStage.ARTIFACTS_ONBOARDING_LAB)}
                        onChange={() => handleWeeklyCheckboxChange(NotificationStage.ARTIFACTS_ONBOARDING_LAB)}
                      >
                        Artifacts Onboarding LAB
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="weekly-cb5"
                        checked={weeklyCustomStages.includes(NotificationStage.ARTIFACTS_DEPLOYMENT)}
                        onChange={() => handleWeeklyCheckboxChange(NotificationStage.ARTIFACTS_DEPLOYMENT)}
                      >
                        Artifacts Deployment
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="weekly-cb6"
                        checked={weeklyCustomStages.includes(NotificationStage.TESTING)}
                        onChange={() => handleWeeklyCheckboxChange(NotificationStage.TESTING)}
                      >
                        Testing
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="weekly-cb7"
                        checked={weeklyCustomStages.includes(NotificationStage.ARTIFACTS_RETRIEVING_PROD)}
                        onChange={() => handleWeeklyCheckboxChange(NotificationStage.ARTIFACTS_RETRIEVING_PROD)}
                      >
                        Artifacts Retrieving PROD
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="weekly-cb8"
                        checked={weeklyCustomStages.includes(NotificationStage.ARTIFACTS_ONBOARDING_PROD)}
                        onChange={() => handleWeeklyCheckboxChange(NotificationStage.ARTIFACTS_ONBOARDING_PROD)}
                      >
                        Artifacts Onboarding PROD
                      </ReactEDSWrapper>
                    </div>
                  )}
                </div>
              </div>
            )}

            <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: '20px 0 16px 0' }} />
            <div className="title" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', marginTop: '20px' }}>
              <span>Daily Email</span>
              <ReactEDSWrapper
                component={Switch}
                enabledLabel="On"
                disabledLabel="Off"
                onChange={handleDailyEmailSwitch}
                checked={dailyEmailEnabled}
              />
            </div>
            {dailyEmailEnabled && (
              <div className="item">
                <div className="left">
                  <div className={styles.flexColumn}>
                    <ReactEDSWrapper
                      component={RadioButton}
                      id="daily-rb1"
                      name="dailyGroup"
                      defaultChecked={dailyNotificationType === NotificationType.ALL}
                      onChange={() => handleDailyRadioChange(NotificationType.ALL)}
                    >
                      All Notifications
                    </ReactEDSWrapper>
                    <ReactEDSWrapper
                      component={RadioButton}
                      id="daily-rb2"
                      name="dailyGroup"
                      defaultChecked={dailyNotificationType === NotificationType.FAILED_STAGES}
                      onChange={() => handleDailyRadioChange(NotificationType.FAILED_STAGES)}
                    >
                      Only Failed Stages
                    </ReactEDSWrapper>
                    <ReactEDSWrapper
                      component={RadioButton}
                      id="daily-rb3"
                      name="dailyGroup"
                      defaultChecked={dailyNotificationType === NotificationType.CUSTOM}
                      onChange={() => handleDailyRadioChange(NotificationType.CUSTOM)}
                    >
                      Custom Options
                    </ReactEDSWrapper>
                  </div>
                  {dailyNotificationType === NotificationType.CUSTOM && (
                    <div className={styles.flexColumnIndented}>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="daily-cb1"
                        checked={dailyCustomStages.includes(NotificationStage.SW_DISTRIBUTION)}
                        onChange={() => handleDailyCheckboxChange(NotificationStage.SW_DISTRIBUTION)}
                      >
                        SW Distribution
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="daily-cb2"
                        checked={dailyCustomStages.includes(NotificationStage.OCI_REGISTRY)}
                        onChange={() => handleDailyCheckboxChange(NotificationStage.OCI_REGISTRY)}
                      >
                        OCI Registry
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="daily-cb3"
                        checked={dailyCustomStages.includes(NotificationStage.ARTIFACTS_RETRIEVING_LAB)}
                        onChange={() => handleDailyCheckboxChange(NotificationStage.ARTIFACTS_RETRIEVING_LAB)}
                      >
                        Artifacts Retrieving LAB
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="daily-cb4"
                        checked={dailyCustomStages.includes(NotificationStage.ARTIFACTS_ONBOARDING_LAB)}
                        onChange={() => handleDailyCheckboxChange(NotificationStage.ARTIFACTS_ONBOARDING_LAB)}
                      >
                        Artifacts Onboarding LAB
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="daily-cb5"
                        checked={dailyCustomStages.includes(NotificationStage.ARTIFACTS_DEPLOYMENT)}
                        onChange={() => handleDailyCheckboxChange(NotificationStage.ARTIFACTS_DEPLOYMENT)}
                      >
                        Artifacts Deployment
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="daily-cb6"
                        checked={dailyCustomStages.includes(NotificationStage.TESTING)}
                        onChange={() => handleDailyCheckboxChange(NotificationStage.TESTING)}
                      >
                        Testing
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="daily-cb7"
                        checked={dailyCustomStages.includes(NotificationStage.ARTIFACTS_RETRIEVING_PROD)}
                        onChange={() => handleDailyCheckboxChange(NotificationStage.ARTIFACTS_RETRIEVING_PROD)}
                      >
                        Artifacts Retrieving PROD
                      </ReactEDSWrapper>
                      <ReactEDSWrapper
                        component={Checkbox}
                        id="daily-cb8"
                        checked={dailyCustomStages.includes(NotificationStage.ARTIFACTS_ONBOARDING_PROD)}
                        onChange={() => handleDailyCheckboxChange(NotificationStage.ARTIFACTS_ONBOARDING_PROD)}
                      >
                        Artifacts Onboarding PROD
                      </ReactEDSWrapper>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="title" style={{ marginBottom: '16px', marginTop: '20px' }}>
              User Interface
            </div>
            <div className="item">
              <div className="left">
                <ReactEDSWrapper component={Checkbox} id="cb7" checked={silentMode} onChange={handleSilentModeChange}>
                  <span>Silent Mode</span>
                  <i className={styles.silentModeIcon + ' icon icon-info'}></i>
                </ReactEDSWrapper>
              </div>
            </div>
          </div>

          <div className="bottom">
            <ReactEDSWrapper component={Button} type="button" onClick={handleSave} disabled={!hasUnsavedChanges}>
              Save
            </ReactEDSWrapper>
            <ReactEDSWrapper component={Button} type="button" onClick={signOut}>
              <i className="icon icon-logout"></i> Sign out
            </ReactEDSWrapper>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog for unsaved changes */}
      <ReactEDSWrapper
        component={Dialog}
        show={showClosePrompt}
        title="Unsaved Changes"
        onClose={handlePromptDiscard}
        bottom={
          <ReactEDSWrapper component={Button} primary type="button" onClick={handleSaveOnPrompt}>
            Save
          </ReactEDSWrapper>
        }
        closeButtonLabel="Discard Changes"
      >
        <p>
          Please save your changes before navigating away. <br></br> Unsaved data will be lost.
        </p>
      </ReactEDSWrapper>
    </div>
  );
};

export default SystemPanel;
